import { useState } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

interface VocabHints {
  part_of_speech_domain: string;
  etymology: string;
  usage_example: string;
}

interface VocabData {
  word: string;
  pronunciation: string;
  part_of_speech: string;
  difficulty: string;
  hints: VocabHints;
  choices: string[];
}

interface CheckResult {
  correct: boolean;
  correct_index: number;
  definition: string;
}

interface SavedState {
  word: string;
  revealedHints: number[];
  selectedIndex: number;
  result: CheckResult;
}

interface Props {
  nanoid: string;
  vocab: VocabData;
}

const DIFF_STYLE: Record<string, { color: string; bg: string }> = {
  easy:   { color: '#2d6e4e', bg: '#e8f5ee' },
  medium: { color: '#7a5200', bg: '#fff3d6' },
  hard:   { color: '#a02020', bg: '#fdecea' },
};

// The three hint dimensions, in display order. Players spend a budget of 3 hints
// to unlock these in whatever order they like.
const HINT_META: { key: keyof VocabHints; emoji: string; label: string }[] = [
  { key: 'part_of_speech_domain', emoji: '🏷️', label: 'Part of speech & domain' },
  { key: 'etymology',             emoji: '📜', label: 'Etymology' },
  { key: 'usage_example',         emoji: '✍️', label: 'Usage example' },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function VocabPuzzle({ nanoid, vocab }: Props) {
  const { word, pronunciation, part_of_speech, difficulty, hints, choices } = vocab ?? {};
  const lsKey = `pz_vocab_${nanoid}`;
  const diffStyle = DIFF_STYLE[difficulty] ?? DIFF_STYLE.hard;

  // Restore a completed game from localStorage (keyed on the word so a new day resets).
  const restored = (() => {
    try {
      const raw = localStorage.getItem(lsKey);
      if (raw) {
        const s: SavedState = JSON.parse(raw);
        if (s.word === word) return s;
      }
    } catch {}
    return null;
  })();

  const [revealedHints, setRevealedHints] = useState<number[]>(restored?.revealedHints ?? []);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(restored?.selectedIndex ?? null);
  const [result, setResult] = useState<CheckResult | null>(restored?.result ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hintsUsed = revealedHints.length;
  const answered = result !== null;

  function spendHint(i: number) {
    if (answered || revealedHints.includes(i)) return;
    setRevealedHints(prev => [...prev, i]);
  }

  async function choose(i: number) {
    if (answered || loading) return;
    setSelectedIndex(i);
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/puzzles/${nanoid}/vocab/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ choice_index: i }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');
      const r: CheckResult = data.data;
      setResult(r);
      try {
        localStorage.setItem(
          lsKey,
          JSON.stringify({ word, revealedHints, selectedIndex: i, result: r } satisfies SavedState),
        );
      } catch {}
    } catch {
      setSelectedIndex(null);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Reward message: the fewer hints used, the better — a no-hint first guess is the prize.
  function verdict(): { text: string; tone: 'win' | 'good' | 'miss' } {
    if (!result) return { text: '', tone: 'miss' };
    if (!result.correct) return { text: 'Not quite — the correct definition is highlighted below.', tone: 'miss' };
    if (hintsUsed === 0) return { text: 'Flawless — first guess, no hints! 🏆', tone: 'win' };
    if (hintsUsed === 1) return { text: 'Nailed it with just one hint.', tone: 'good' };
    return { text: `Solved it using ${hintsUsed} hints.`, tone: 'good' };
  }
  const v = verdict();

  return (
    <div className="pz-vocab">
      {/* Word block — always visible */}
      <div className="pz-vocab-word-block">
        <p className="pz-vocab-label">Word of the day</p>
        <p className="pz-vocab-word">{word}</p>
        <div className="pz-vocab-meta">
          {pronunciation && <span className="pz-vocab-pron">{pronunciation}</span>}
          {part_of_speech && <span className="pz-vocab-pos">{part_of_speech}</span>}
          <span className="pz-difficulty-badge" style={{ color: diffStyle.color, background: diffStyle.bg }}>
            {difficulty?.charAt(0).toUpperCase()}{difficulty?.slice(1)}
          </span>
        </div>
        <p className="pz-vocab-prompt">
          Do you know what it means? Spend hints if you need them — then pick the definition.
        </p>
      </div>

      {/* Hint budget */}
      <div className="pz-vocab-hints">
        <div className="pz-vocab-hints-head">
          <span className="pz-vocab-hints-title">Hints</span>
          <span className="pz-vocab-hints-budget">{3 - hintsUsed} of 3 left</span>
        </div>
        {HINT_META.map((h, i) => {
          const open = revealedHints.includes(i);
          return open ? (
            <div key={h.key} className="pz-vocab-hint pz-vocab-hint-open">
              <span className="pz-vocab-hint-emoji">{h.emoji}</span>
              <span className="pz-vocab-hint-body">
                <span className="pz-vocab-hint-label">{h.label}</span>
                <span className="pz-vocab-hint-text">{hints?.[h.key]}</span>
              </span>
            </div>
          ) : (
            <button
              key={h.key}
              className="pz-vocab-hint pz-vocab-hint-locked"
              onClick={() => spendHint(i)}
              disabled={answered}
            >
              <span className="pz-vocab-hint-emoji">{h.emoji}</span>
              <span className="pz-vocab-hint-body">
                <span className="pz-vocab-hint-label">{h.label}</span>
                <span className="pz-vocab-hint-text pz-vocab-hint-muted">
                  {answered ? 'Not used' : 'Tap to reveal'}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Multiple choice */}
      <div className="pz-vocab-choices">
        <p className="pz-vocab-choices-label">What does it mean?</p>
        {(choices ?? []).map((c, i) => {
          const isSelected = selectedIndex === i;
          const isCorrect = answered && result?.correct_index === i;
          const isWrongPick = answered && isSelected && !result?.correct;
          const cls = [
            'pz-vocab-choice',
            isCorrect ? 'pz-vocab-choice-correct' : '',
            isWrongPick ? 'pz-vocab-choice-wrong' : '',
            answered && !isCorrect && !isWrongPick ? 'pz-vocab-choice-dim' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={i}
              className={cls}
              onClick={() => choose(i)}
              disabled={answered || loading}
            >
              <span className="pz-vocab-choice-mark">{String.fromCharCode(65 + i)}</span>
              <span className="pz-vocab-choice-text">{c}</span>
              {isCorrect && <span className="pz-vocab-choice-tick">✓</span>}
              {isWrongPick && <span className="pz-vocab-choice-tick">✗</span>}
            </button>
          );
        })}
      </div>

      {error && <p className="pz-error">{error}</p>}

      {/* Verdict */}
      {answered && (
        <div className={`pz-vocab-verdict pz-vocab-verdict-${v.tone}`}>
          <p className="pz-vocab-verdict-text">{v.text}</p>
          {result?.definition && (
            <p className="pz-vocab-definition">
              <strong>{word}</strong> — {result.definition}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
